import { useEffect, useState, useRef } from "react";
import { Navbar } from "@/components/Navbar";
import { KitchenCard } from "@/components/KitchenCard";
import { BrowseFilters } from "@/components/BrowseFilters";
import { BrowseMap } from "@/components/BrowseMap";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Browse() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [maxRate, setMaxRate] = useState("");
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [sort, setSort] = useState("");
  const [view, setView] = useState("grid");
  const [loading, setLoading] = useState(true);
  const [savingAlert, setSavingAlert] = useState(false);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (city) params.city = city;
    if (stateFilter) params.state = stateFilter;
    if (maxRate) params.max_rate = Number(maxRate);
    if (selectedAmenities.length) params.amenities = selectedAmenities.join(",");
    if (verifiedOnly) params.verified_only = true;
    if (sort) params.sort = sort;
    const { data } = await api.get("/kitchens", { params });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Auto-reload when sort or verified toggle changes (skip initial mount — first effect handles that).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, verifiedOnly]);

  const toggleAmenity = (a) => {
    setSelectedAmenities((prev) => prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]);
  };

  const saveAlert = async () => {
    if (!user) { toast.error("Sign in as a cook to save alerts"); return; }
    if (user.role !== "cook") { toast.error("Only cooks can save alerts"); return; }
    if (!q && !city && !maxRate && selectedAmenities.length === 0) {
      toast.error("Pick at least one filter first");
      return;
    }
    setSavingAlert(true);
    try {
      await api.post("/alerts", {
        q, city,
        max_rate: maxRate ? Number(maxRate) : null,
        amenities: selectedAmenities,
      });
      toast.success("Alert saved — we'll email you when a match appears");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSavingAlert(false); }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <BrowseFilters
          q={q} setQ={setQ}
          city={city} setCity={setCity}
          stateFilter={stateFilter} setStateFilter={setStateFilter}
          maxRate={maxRate} setMaxRate={setMaxRate}
          selectedAmenities={selectedAmenities} toggleAmenity={toggleAmenity}
          verifiedOnly={verifiedOnly} setVerifiedOnly={setVerifiedOnly}
          sort={sort} setSort={setSort}
          onApply={load}
          onSaveAlert={saveAlert} savingAlert={savingAlert}
          view={view} setView={setView}
        />

        {loading ? (
          <div className="mt-14 tick-label">Loading...</div>
        ) : items.length === 0 ? (
          <div className="mt-14 text-center border hairline py-20">
            <div className="font-display text-3xl font-bold">No kitchens found</div>
            <div className="text-neutral-500 mt-3">Try widening your filters or being the first to list a kitchen.</div>
          </div>
        ) : view === "grid" ? (
          <div data-testid="kitchen-grid" className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((k) => <KitchenCard key={k.id} k={k} />)}
          </div>
        ) : (
          <BrowseMap items={items} />
        )}
      </div>
    </div>
  );
}
